import { useEffect, useMemo, useState } from 'react';
import { DownloadSimple, PlusCircle, Tag, Trash } from 'shared/icons';
import api from '../../../services/api';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import Modal from '../../ui/Modal';
import { Mono } from '../../ui/Mono';
import Select from '../../ui/Select';
import { useToast } from '../../ui/Toast';
import { LocationPicker, ProductPicker, useActiveDeposits, useActiveLocations, useActiveLpnPrefixes } from '../lpnPickers';
import { DownloadZplFile, SendZplToPrinter, useZplPrinters, ZplPrinterSelect } from '../zplPrinting';

/**
 * Registrar recebimento físico contra a OC — o ATO do inbound.
 *
 * Uma confirmação = UM pallet (LPN novo, origin PURCHASE_ORDER): quem recebe
 * em vários pallets registra um de cada vez (parcial é o caso NORMAL). As
 * linhas nascem pré-preenchidas com o que FALTA de cada item da OC (quem
 * recebeu tudo só confirma); linha avulsa (produto fora do pedido) entra pelo
 * ProductPicker e vira warning NOT_ON_ORDER — divergência NUNCA trava.
 *
 * Passo 2 (pós-criação) = etiqueta na hora, pelo MESMO fluxo de impressão do
 * M1/M3 (rede + USB do QZ) — nenhum segundo caminho de impressão.
 */
const InboundReceiveModal = ({ isOpen, onClose, order, items = [], label, onCreated }) => {
    const toast = useToast();
    const [prefixCode, setPrefixCode] = useState('REV');
    const [locationId, setLocationId] = useState('');
    const [depositId, setDepositId] = useState('');
    const [invoiceId, setInvoiceId] = useState('');
    const [lines, setLines] = useState([]);
    const [extraLines, setExtraLines] = useState([]);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState(null);
    const [created, setCreated] = useState(null);
    const [warnings, setWarnings] = useState([]);
    const [sending, setSending] = useState(false);

    const prefixes = useActiveLpnPrefixes(isOpen);
    const deposits = useActiveDeposits(isOpen);
    const locations = useActiveLocations(isOpen);
    const { printers, localPrinters, selectedPrinter, setSelectedPrinter } = useZplPrinters(Boolean(created));

    const invoices = order?.invoices || [];

    // As linhas nascem do que FALTA (pedido − recebido); zero fica de fora do
    // payload, mas continua na tela para o usuário ENXERGAR o que já fechou.
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setLines(
            items.map((item) => ({
                purchase_order_item_id: item.id,
                sku: item.sku,
                product_id: item.product_id,
                title: item.product_title,
                pending: Math.max(Number(item.quantity_ordered || 0) - Number(item.quantity_received || 0), 0),
                qty: String(Math.max(Number(item.quantity_ordered || 0) - Number(item.quantity_received || 0), 0)),
                supplier_lot: '',
            })),
        );
        setExtraLines([]);
        setCreated(null);
        setWarnings([]);
        setFormError(null);
        setInvoiceId('');
    }, [isOpen, items]);

    useEffect(() => {
        setDepositId((previous) => previous || (deposits.rows[0]?.id ? String(deposits.rows[0].id) : ''));
    }, [deposits.rows]);

    const totalQty = useMemo(
        () =>
            lines.reduce((total, line) => total + (Number(line.qty) || 0), 0) +
            extraLines.reduce((total, line) => total + (Number(line.qty) || 0), 0),
        [lines, extraLines],
    );

    const closeModal = () => {
        if (!saving && !sending) {
            onClose?.();
        }
    };

    const setLineQty = (index, value) => {
        setLines((previous) => previous.map((line, i) => (i === index ? { ...line, qty: value } : line)));
    };
    const setLineLot = (index, value) => {
        setLines((previous) => previous.map((line, i) => (i === index ? { ...line, supplier_lot: value } : line)));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setFormError(null);

        if (!locationId) {
            setFormError({ message: `${label('wms.lpn.field.location')}: ${label('wms.common.required')}` });
            return;
        }
        if (!depositId) {
            setFormError({ message: `${label('wms.lpn.field.deposit')}: ${label('wms.common.required')}` });
            return;
        }

        const payloadItems = [
            ...lines
                .filter((line) => Number(line.qty) > 0)
                .map((line) => ({
                    purchase_order_item_id: line.purchase_order_item_id,
                    qty: Number(line.qty),
                    ...(line.supplier_lot.trim() ? { supplier_lot: line.supplier_lot.trim() } : {}),
                })),
            ...extraLines
                .filter((line) => line.product?.product_id && Number(line.qty) > 0)
                .map((line) => ({
                    product_id: String(line.product.product_id),
                    qty: Number(line.qty),
                })),
        ];
        if (payloadItems.length === 0) {
            setFormError({ message: label('wms.lpn_create.items_required') });
            return;
        }

        setSaving(true);
        try {
            const data = await api.createWarehouseInboundReceipt({
                purchase_order_id: order.id,
                prefix_code: prefixCode,
                location_id: locationId,
                deposit_id: depositId,
                ...(invoiceId ? { invoice_id: invoiceId } : {}),
                items: payloadItems,
            });
            setCreated(data?.lpn || null);
            setWarnings(Array.isArray(data?.warnings) ? data.warnings : []);
            toast.success(label('wms.inbound.receive_success'));
            onCreated?.();
        } catch (error) {
            setFormError({ message: error?.message || 'Erro ao salvar' });
        } finally {
            setSaving(false);
        }
    };

    const handlePrint = async () => {
        if (!created?.zpl || !selectedPrinter) {
            return;
        }
        setSending(true);
        try {
            const printerName = await SendZplToPrinter({ target: selectedPrinter, printers, zpl: created.zpl, label });
            toast.success(`${label('wms.labels.sent')}${printerName ? ` — ${printerName}` : ''}`);
        } catch (error) {
            toast.error(error);
        } finally {
            setSending(false);
        }
    };

    const createdStep = created ? (
        <div className="space-y-4">
            <div className="space-y-2 rounded-lg border border-mono-200 bg-mono-50 p-4 text-center dark:border-mono-800 dark:bg-mono-950">
                <p className="text-xs font-medium uppercase tracking-wide text-mono-500 dark:text-mono-400">
                    {label('wms.lpn_create.created_title')}
                </p>
                <Mono className="block text-2xl font-semibold text-black dark:text-white">{created.code}</Mono>
                <div className="flex flex-wrap items-center justify-center gap-1.5">
                    {created.location_code ? <Badge variant="neutral">{created.location_code}</Badge> : null}
                    {created.deposit_code ? <Badge variant="neutral">{created.deposit_code}</Badge> : null}
                </div>
            </div>

            {warnings.length > 0 ? (
                <div className="space-y-1 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950/40">
                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                        {label('wms.inbound.warning_title')}
                    </p>
                    {warnings.map((warning, index) => (
                        <p key={index} className="text-sm text-amber-800 dark:text-amber-200">
                            {warning.message}
                        </p>
                    ))}
                </div>
            ) : null}

            {created.zpl ? (
                <>
                    <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.lpn_create.print_hint')}</p>
                    <ZplPrinterSelect
                        label={label}
                        printers={printers}
                        localPrinters={localPrinters}
                        value={selectedPrinter}
                        onChange={setSelectedPrinter}
                        disabled={sending}
                    />
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <Button
                            type="button"
                            onClick={handlePrint}
                            loading={sending}
                            disabled={!selectedPrinter}
                            className="flex-1"
                        >
                            <Tag size={18} className="mr-2" />
                            {label('wms.labels.send')}
                        </Button>
                        <Button
                            type="button"
                            variant="secondary"
                            disabled={sending}
                            onClick={() => DownloadZplFile(created.zpl, `lpn-${created.code}.zpl`)}
                        >
                            <DownloadSimple size={18} className="mr-2" />
                            {label('wms.labels.download')}
                        </Button>
                    </div>
                </>
            ) : null}

            <Button type="button" variant="secondary" onClick={closeModal} disabled={sending} className="w-full">
                {label('wms.lpn_create.finish')}
            </Button>
        </div>
    ) : null;

    const formStep = (
        <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.inbound.receive_hint')}</p>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Select
                    label={label('wms.lpn_create.prefix')}
                    value={prefixCode}
                    onChange={(event) => setPrefixCode(event.target.value)}
                    disabled={saving}
                >
                    {prefixes.rows.map((prefix) => (
                        <option key={prefix.id} value={prefix.code}>
                            {prefix.code} — {prefix.label}
                        </option>
                    ))}
                </Select>
                <LocationPicker
                    label={label}
                    locations={locations.rows}
                    loading={locations.loading}
                    value={locationId}
                    onChange={setLocationId}
                    disabled={saving}
                />
                <Select
                    label={label('wms.lpn.field.deposit')}
                    value={depositId}
                    onChange={(event) => setDepositId(event.target.value)}
                    disabled={saving}
                >
                    {deposits.rows.map((deposit) => (
                        <option key={deposit.id} value={String(deposit.id)}>
                            {deposit.code} — {deposit.label}
                        </option>
                    ))}
                </Select>
            </div>

            {invoices.length > 0 ? (
                <Select
                    label={label('wms.inbound.invoices_title')}
                    value={invoiceId}
                    onChange={(event) => setInvoiceId(event.target.value)}
                    disabled={saving}
                >
                    <option value="">—</option>
                    {invoices.map((invoice) => (
                        <option key={invoice.id} value={invoice.id}>
                            NF {invoice.number || invoice.access_key.slice(25, 34)}
                            {invoice.total_amount ? ` — R$ ${invoice.total_amount}` : ''}
                        </option>
                    ))}
                </Select>
            ) : null}

            <div className="overflow-x-auto rounded-lg border border-mono-200 dark:border-mono-800">
                <table className="w-full text-sm">
                    <thead className="bg-mono-50 text-left text-xs uppercase tracking-wide text-mono-500 dark:bg-mono-950 dark:text-mono-400">
                        <tr>
                            <th className="px-3 py-2">{label('wms.inbound.field.sku')}</th>
                            <th className="px-3 py-2 text-right">{label('wms.inbound.field.pending')}</th>
                            <th className="px-3 py-2">{label('wms.lpn.field.qty')}</th>
                            <th className="px-3 py-2">{label('wms.lpn.field.supplier_lot')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-mono-100 dark:divide-mono-900">
                        {lines.map((line, index) => (
                            <tr key={line.purchase_order_item_id} className={line.product_id ? '' : 'opacity-60'}>
                                <td className="px-3 py-2">
                                    <Mono>{line.sku}</Mono>
                                    {line.title ? (
                                        <p className="max-w-[260px] truncate text-xs text-mono-500 dark:text-mono-400">
                                            {line.title}
                                        </p>
                                    ) : null}
                                    {!line.product_id ? (
                                        <p className="text-xs text-amber-600 dark:text-amber-400">
                                            {label('wms.inbound.status.NOT_ON_ORDER')}
                                        </p>
                                    ) : null}
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <Mono>{line.pending}</Mono>
                                </td>
                                <td className="px-3 py-2">
                                    <Input
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={line.qty}
                                        onChange={(event) => setLineQty(index, event.target.value)}
                                        disabled={saving || !line.product_id}
                                        className="w-24"
                                    />
                                </td>
                                <td className="px-3 py-2">
                                    <Input
                                        value={line.supplier_lot}
                                        onChange={(event) => setLineLot(index, event.target.value)}
                                        placeholder="—"
                                        disabled={saving || !line.product_id}
                                        className="w-32"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Fora do pedido: registra e segue — o desvio fica à vista. */}
            <div className="space-y-2">
                {extraLines.map((line, index) => (
                    <div key={index} className="flex items-end gap-2">
                        <div className="min-w-0 flex-1">
                            <ProductPicker
                                label={label}
                                fieldLabel={label('wms.inbound.status.NOT_ON_ORDER')}
                                value={line.product}
                                onChange={(product) =>
                                    setExtraLines((previous) =>
                                        previous.map((entry, i) => (i === index ? { ...entry, product } : entry)),
                                    )
                                }
                                disabled={saving}
                            />
                        </div>
                        <Input
                            type="number"
                            min="1"
                            step="1"
                            value={line.qty}
                            onChange={(event) =>
                                setExtraLines((previous) =>
                                    previous.map((entry, i) =>
                                        i === index ? { ...entry, qty: event.target.value } : entry,
                                    ),
                                )
                            }
                            disabled={saving}
                            className="w-24"
                        />
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setExtraLines((previous) => previous.filter((_entry, i) => i !== index))}
                            disabled={saving}
                        >
                            <Trash size={16} />
                        </Button>
                    </div>
                ))}
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={saving}
                    onClick={() => setExtraLines((previous) => [...previous, { product: null, qty: '1' }])}
                >
                    <PlusCircle size={16} className="mr-2" />
                    {label('wms.inbound.status.NOT_ON_ORDER')}
                </Button>
            </div>

            {formError ? <p className="text-sm text-red-600 dark:text-red-400">{formError.message}</p> : null}

            <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-mono-600 dark:text-mono-400">
                    Total: <Mono className="font-semibold">{totalQty}</Mono> un
                </p>
                <div className="flex gap-2">
                    <Button type="button" variant="secondary" onClick={closeModal} disabled={saving}>
                        {label('wms.common.cancel')}
                    </Button>
                    <Button type="submit" loading={saving} disabled={totalQty <= 0}>
                        {label('wms.inbound.receive_confirm')}
                    </Button>
                </div>
            </div>
        </form>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={closeModal}
            title={
                created
                    ? label('wms.lpn_create.created_title')
                    : `${label('wms.inbound.receive_title')} — ${order?.code || ''}`
            }
            size="2xl"
        >
            {created ? createdStep : formStep}
        </Modal>
    );
};

export default InboundReceiveModal;
